import { Component, inject, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { WorkAddressSearchResultDto, WorkAddressService } from '@work-frontend-monorepo/work-rest-api';
import { lastValueFrom } from 'rxjs';
import { InputFieldWithOptionsComponent } from '../input-field-with-options/input-field-with-options.component';
import { IconComponent } from '../../icon/icon.component';
import { DropdownOption } from '../../../models/dropdown-option.model';
import { LoadingAndFeedbackState } from '../../../models/loading-and-feedback-state.type';
import { ExceptionHandlingService } from '../../../services/exception-handling/exception-handling.service';

/**
 * Reusable address autocomplete backed by the Google Places API (via the backend `/v2/address/search` endpoint).
 *
 * Renders a single search input with a dynamic dropdown. When the user picks a suggestion the full
 * structured address (street, streetNumber, zip, city, canton, coordinates, ...) is emitted via
 * `addressSelected`. The consuming component is responsible for mapping the result into its own form
 * controls (field names differ per entity, e.g. `objectStreet` vs `street`).
 */
@Component({
  selector: 'work-ui-address-autocomplete',
  standalone: true,
  imports: [TranslateModule, InputFieldWithOptionsComponent, IconComponent],
  templateUrl: './address-autocomplete.component.html',
  styleUrl: './address-autocomplete.component.scss',
})
export class AddressAutocompleteComponent {
  public id = input<string>('addressAutocomplete');
  public name = input<string>('addressAutocomplete');
  public label = input<string>('ADRESSE_SUCHEN');
  public placeholder = input<string>('ADRESSE_SUCHEN_PLACEHOLDER');
  public required = input<boolean>(false);
  public disabled = input<boolean>(false);

  public addressSelected = output<WorkAddressSearchResultDto>();

  protected readonly dropdownOptions = signal<DropdownOption[]>([]);
  protected readonly loadingState = signal<LoadingAndFeedbackState>('none');

  private readonly addressService = inject(WorkAddressService);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);

  protected async onSearchChanged(query: string): Promise<void> {
    if (!query || query.length === 0) {
      this.dropdownOptions.set([]);
      this.loadingState.set('none');
      return;
    }

    this.loadingState.set('loading');
    let results: WorkAddressSearchResultDto[] = [];
    try {
      results = await lastValueFrom(this.addressService.addressV2ControllerSearchAddress({ query }));
    } catch (searchException) {
      this.exceptionHandlingService.handleExceptionWithAlert('AddressAutocompleteSearch', searchException);
    }

    this.dropdownOptions.set(results.map((result) => ({ value: result, label: result.formattedAddress })));
    this.loadingState.set('none');
  }

  protected onOptionSelected(option: DropdownOption): void {
    this.addressSelected.emit(option.value as WorkAddressSearchResultDto);
  }
}
